import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Flame, Lock } from "lucide-react";
import { API } from "../App";

/**
 * FoundingSpotsCounter — public progress bar showing how many of the first
 * 100 founding-member spots remain. Renders three variants via `variant`:
 *
 *  - `hero`     — full-width pill at the top of the landing page
 *  - `pricing`  — compact card slotted above the tier grid
 *  - `inline`   — small footnote variant, used inside other cards
 *
 * Data comes from GET /api/founding-spots/public (no auth).
 * Counter auto-refreshes on window focus and every 60s so it stays current
 * without polling aggressively.
 *
 * When `closed=true`, the bar disappears and is replaced with a quiet
 * "Founding member spots are now closed" notice.
 */
const FoundingSpotsCounter = ({ variant = "hero", className = "" }) => {
  const [status, setStatus] = useState(null);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/founding-spots/public`);
      setStatus(r.data);
    } catch {
      /* silent — counter is non-critical */
    }
  };

  useEffect(() => {
    fetchStatus();
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    const t = setInterval(fetchStatus, 60_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, []);

  if (!status) return null;
  const { total, remaining, closed, urgency_message, urgency_level } = status;
  const pct = total > 0 ? Math.min(100, Math.max(0, ((total - remaining) / total) * 100)) : 0;

  // Closed state — quiet notice, no progress bar.
  if (closed) {
    return (
      <div
        className={`flex items-center justify-center gap-2 rounded-2xl border border-[#E5DFEC] bg-[#FAF7F2] px-4 py-3 text-sm text-[#64748B] ${className}`}
        data-testid="founding-spots-closed"
      >
        <Lock className="w-4 h-4 text-[#94A3B8]" />
        <span>{urgency_message}</span>
      </div>
    );
  }

  // Urgency-driven accent colour — sage at high counts, plum/red as it shrinks.
  const urgencyAccent = {
    critical: "#DC2626",  // warm red, last 10
    high:     "#D97706",  // amber, last 25
    medium:   "#6B4E8A",  // plum, last 50
    normal:   "#8DB5A0",  // sage, comfortable cushion
  }[urgency_level] || "#8DB5A0";

  const isCompact = variant === "inline";
  const headlinePrefix = variant === "pricing" ? "Founding rate" : "Founding Member Spots";

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl bg-white border border-[#E5DFEC] ${isCompact ? "p-3" : "p-4 sm:p-5"} shadow-sm ${className}`}
      data-testid={`founding-spots-${variant}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 mb-2">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0`}
          style={{ backgroundColor: `${urgencyAccent}1A` }}
        >
          <Flame className="w-4 h-4" style={{ color: urgencyAccent }} />
        </div>
        <div className="flex-1 flex flex-wrap items-baseline gap-x-2">
          <span
            className="text-xs uppercase tracking-widest font-semibold"
            style={{ color: urgencyAccent, fontFamily: "Outfit, sans-serif" }}
          >
            {headlinePrefix}
          </span>
          <span
            className="text-sm sm:text-base font-medium text-[#6B4E8A]"
            data-testid="founding-spots-count"
          >
            <strong style={{ color: urgencyAccent }}>{remaining}</strong>
            <span className="text-[#94A3B8]"> / {total} remaining</span>
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 w-full rounded-full bg-[#F0ECF7] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-full rounded-full"
          style={{ backgroundColor: urgencyAccent }}
          data-testid="founding-spots-bar-fill"
        />
      </div>

      {urgency_message && (
        <p
          className="text-xs mt-2 leading-relaxed"
          style={{ color: urgencyAccent }}
          data-testid="founding-spots-urgency"
        >
          {urgency_message}
        </p>
      )}
    </motion.div>
  );
};

export default FoundingSpotsCounter;
